import { differenceInDays } from "date-fns";
import pluralize from "pluralize";
import React from "react";

import { displayDateTime, displaySpaceLayout } from ".";
import {
  Column,
  Container,
  cssVariables,
  Icon,
  Image,
  Row,
  Spacing,
  Text,
} from "../components";
import {
  BookingType,
  getFirstBookingFromOrder,
  getFirstOrderItemBookingFromOrder,
  Order,
  OrderStatus,
  PaymentMethod as PaymentMethodType,
} from "../data";
import { useGetSpace } from "../data/api";
import { getMaxCapacityLabel } from "../data/space";
import { formatCurrency } from "../lib/currency/currency";
import { parseDay } from "../lib/datetime";
import { getSystemLocale } from "../lib/locale/locale";

interface PaymentMethodProps {
  confirmedBookingWoPayment: boolean;
  paymentMethodDetails: PaymentMethodType | null;
}

function PaymentMethod(props: PaymentMethodProps) {
  const { confirmedBookingWoPayment, paymentMethodDetails } = props;

  if (confirmedBookingWoPayment) {
    return <Text fontSize="sm">Confirmed without payment</Text>;
  }

  if (!paymentMethodDetails || !paymentMethodDetails.card) {
    return <Text fontSize="sm">N/A</Text>;
  }

  const { last4, network, networkLogoUrl } = paymentMethodDetails.card;

  if (!networkLogoUrl) {
    return <Text fontSize="sm">Card Ending with {last4}</Text>;
  }

  return (
    <Container>
      <Row alignItems="center">
        <Image width={34} src={networkLogoUrl} alt={network} />
        <Spacing width={10} />
        <Text fontSize="sm">Ending with {last4}</Text>
      </Row>
    </Container>
  );
}

interface OrderDetailsProps {
  order: Order;
  view?: "guest" | "admin";
}

export function OrderDetails(props: OrderDetailsProps) {
  const { order, view = "guest" } = props;
  const booking = getFirstBookingFromOrder(order);
  const orderItemBooking = getFirstOrderItemBookingFromOrder(order);
  const space = useGetSpace(booking.spaceID);
  const layout = space.layouts.find((layout) => layout.id === booking.layoutID);

  if (!layout) {
    return <Text>Something went wrong</Text>;
  }

  const days =
    (booking.bookingType === BookingType.Daily &&
      booking.startDate &&
      booking.endDate &&
      differenceInDays(parseDay(booking.endDate), parseDay(booking.startDate)) +
        1) ||
    1;

  return (
    <Container>
      <Row alignItems="center">
        <Icon name="calendar" />
        <Spacing width={16} />
        <Text isBold>
          {displayDateTime(booking)}{" "}
          <Text>
            (
            {booking.bookingType === BookingType.Daily
              ? `${days} ${pluralize("day", days)}`
              : `${booking.duration} ${pluralize(
                  "hour",
                  Number(booking.duration)
                )}`}
            )
          </Text>
        </Text>
      </Row>
      <Spacing height={24} />
      <Container height={104}>
        <Row>
          {space.images[0] && (
            <Image
              width={140}
              src={space.images[0].small.url}
              alt={`${space.name} thumbnail`}
              borderRadius={cssVariables.radius}
            />
          )}
          <Spacing width={16} />
          <Column>
            <Text isBold>{space.name}</Text>
            {space.inventoryCapacity > 1 && <Text>{booking.room}</Text>}
            <Text fontSize="sm">
              {getMaxCapacityLabel(space)}{" "}
              {space.squareFootage && (
                <>&#8226; {space.squareFootage} square ft.</>
              )}
            </Text>
            <Text>
              {formatCurrency(
                orderItemBooking.unitPrice,
                order.currencyCode,
                getSystemLocale()
              )}{" "}
              /{booking.bookingType === BookingType.Daily ? "day" : "hour"}
            </Text>
          </Column>
        </Row>
      </Container>
      {view === "admin" && (
        <>
          <Spacing height={24} />
          <Column expanded>
            <Text fontSize="xs" color="muted">
              Additional notes
            </Text>
            <Spacing height={8} />
            <Text fontSize="sm">{order.additionalNotes || "N/A"}</Text>
          </Column>
        </>
      )}
      <Spacing height={24} />
      <Row>
        <Column expanded>
          <Text fontSize="xs" color="muted">
            Attendees
          </Text>
          <Spacing height={8} />
          <Text fontSize="sm">
            {booking.attendees > 0
              ? `${booking.attendees} ${pluralize(
                  "attendee",
                  booking.attendees
                )}`
              : "N/A"}
          </Text>
        </Column>
        {space.layouts.length > 1 && (
          <Column expanded>
            <Text fontSize="xs" color="muted">
              Layout
            </Text>
            <Spacing height={8} />
            <Text fontSize="sm">{displaySpaceLayout(layout)}</Text>
          </Column>
        )}
      </Row>
      <Spacing height={24} />
      <Row>
        <Column expanded>
          <Text fontSize="xs" color="muted">
            Contact details
          </Text>
          <Spacing height={8} />
          {order.guestName && <Text fontSize="sm">{order.guestName}</Text>}
          {order.guestCompanyName && (
            <Text fontSize="sm">{order.guestCompanyName}</Text>
          )}
          {order.guestEmail && <Text fontSize="sm">{order.guestEmail}</Text>}
          {order.guestPhoneNumber && (
            <Text fontSize="sm">{order.guestPhoneNumber}</Text>
          )}
        </Column>
        <Column expanded>
          <Text fontSize="xs" color="muted">
            Payment method
          </Text>
          <Spacing height={8} />
          <PaymentMethod
            confirmedBookingWoPayment={order.status === OrderStatus.Confirmed}
            paymentMethodDetails={order.paymentMethodDetails}
          />
          <Spacing height={16} />
          <Text fontSize="xs" color="muted">
            Total {order.status === OrderStatus.Confirmed ? "" : "paid"}
          </Text>
          <Spacing height={8} />
          <Text isBold fontSize="sm">
            {formatCurrency(
              order.totalPrice,
              order.currencyCode,
              getSystemLocale()
            )}
          </Text>
          <Spacing height={8} />
        </Column>
      </Row>
    </Container>
  );
}
